import React, { useEffect, useRef, useState } from 'react';

export default function VideoPlayer({ src, initialPosition=0, onStart, onHeartbeat, onComplete, isCompleted }) {
  const videoRef=useRef(null), highWater=useRef(Number(initialPosition)||0), started=useRef(false), starting=useRef(null), queue=useRef(Promise.resolve()), lastSave=useRef(0), seeking=useRef(false), finished=useRef(Boolean(isCompleted));
  const callbacks=useRef({onStart,onHeartbeat,onComplete}); callbacks.current={onStart,onHeartbeat,onComplete};
  const [error,setError]=useState(''),[notice,setNotice]=useState(''),[saving,setSaving]=useState(false),[done,setDone]=useState(Boolean(isCompleted));
  useEffect(()=>()=>{ videoRef.current?.pause(); },[]);
  async function begin() {
    if (started.current || finished.current) return;
    if (!starting.current) {
      videoRef.current.pause();
      starting.current=callbacks.current.onStart().then(()=>{ started.current=true;lastSave.current=Date.now();return videoRef.current?.play(); }).catch(e=>setError(e.response?.data?.error||'The lesson could not start. Press Play to retry.')).finally(()=>{starting.current=null;});
    }
    await starting.current;
  }
  function save(position) {
    if (!started.current || finished.current) return queue.current;
    queue.current=queue.current.catch(()=>{}).then(async()=>{
      try { await callbacks.current.onHeartbeat({positionSeconds:position});setError(''); }
      catch(e) {
        const allowed=Number(e.response?.data?.allowedPositionSeconds);
        if(Number.isFinite(allowed)&&videoRef.current) { videoRef.current.currentTime=allowed;highWater.current=allowed; }
        setError(e.response?.data?.error||'Progress could not be saved. Check your connection and retry.');throw e;
      }
    });
    return queue.current;
  }
  async function finish() {
    if(finished.current||saving)return;
    setSaving(true);
    try {
      await save(videoRef.current.currentTime);
      await callbacks.current.onComplete({positionSeconds:videoRef.current.currentTime});
      finished.current=true;setDone(true);setError('');
    } catch(e) { setError(e.response?.data?.error||'Completion could not be saved. Try saving again.'); }
    finally {setSaving(false);}
  }
  return <div className="lesson-player">
    <video ref={videoRef} src={src} controls playsInline preload="metadata"
      onLoadedMetadata={()=>{if(!finished.current)videoRef.current.currentTime=Math.min(highWater.current,videoRef.current.duration);}}
      onPlay={begin}
      onPause={()=>{if(started.current&&!videoRef.current.ended)save(videoRef.current.currentTime).catch(()=>{});}}
      onSeeking={()=>{seeking.current=true;const v=videoRef.current;if(!finished.current&&v.currentTime>highWater.current+1.5){v.currentTime=highWater.current;setNotice('Watch this section first. You can rewind at any time.');}}}
      onSeeked={()=>{seeking.current=false;}}
      onTimeUpdate={()=>{const v=videoRef.current;if(seeking.current||!started.current)return;highWater.current=Math.max(highWater.current,v.currentTime);if(!v.paused&&Date.now()-lastSave.current>10000){lastSave.current=Date.now();save(v.currentTime).catch(()=>{});}}}
      onEnded={finish}
      onError={()=>setError('This video could not load. Check your connection and refresh the page.')}/>
    <div className="player-status" aria-live="polite">{done?'✓ Lesson completed':saving?'Saving completion…':'Your progress saves as you watch.'}</div>
    {notice&&<p className="player-status">{notice}</p>}
    {error&&<div className="error-banner" role="alert">{error}{videoRef.current?.ended&&<button className="btn btn-outline" onClick={finish} disabled={saving}>Save completion again</button>}</div>}
  </div>;
}
